import React from 'react'

import { readQuery, subscribeQuery, type Query } from '@/lib/queryCache'

const noop = () => () => {}

/**
 * The stored entry a query may show: not the one it refused to serve
 * (past its age, or invalidated) even while that is still stored.
 */
function servable<T>(
    query: Query<T>,
    entry: { value: unknown } | undefined
): { value: T } | undefined {
    return entry && entry !== query.rejected
        ? (entry as { value: T })
        : undefined
}

/** The stored entry for a query's key, re-rendering when it changes. */
function useEntry<T>(query: Query<T>): { value: T } | undefined {
    const { key } = query
    const subscribe = React.useCallback(
        (listener: () => void) =>
            key ? subscribeQuery(key, listener) : noop(),
        [key]
    )
    const read = () => (key ? readQuery(key) : undefined)
    return servable(query, React.useSyncExternalStore(subscribe, read, read))
}

/**
 * A query's value: the stored one (updated as it changes), else the one
 * the loader knew, else the request (suspending until it lands, throwing
 * its failure to the nearest error boundary). <QueryView> wraps both.
 */
export function useQuery<T>(query: Query<T>): T {
    const entry = useEntry(query)
    if (query.initial) return entry ? entry.value : query.initial.value
    // A cold handle waits for whichever comes first: its request, or a
    // servable value reaching the store (a save, another reader). It calls
    // use() on every render, as React requires once it suspended on it.
    const outcome = React.use(firstValue(query))
    if (entry) return entry.value
    if ('value' in outcome) return outcome.value
    if ('error' in outcome) throw outcome.error
    // A stored value that was dropped again: back to the request.
    return React.use(query.promise)
}

type Outcome<T> = { value: T } | { error: unknown } | { stored: true }

/** Per handle, so every render of it waits on the same promise. */
const outcomes = new WeakMap<Query<unknown>, Promise<Outcome<unknown>>>()

function firstValue<T>(query: Query<T>): Promise<Outcome<T>> {
    let outcome = outcomes.get(query as Query<unknown>)
    if (!outcome) {
        outcome = new Promise<Outcome<unknown>>((resolve) => {
            let stop = () => {}
            let settled = false
            const done = (result: Outcome<unknown>) => {
                if (settled) return
                settled = true
                stop()
                resolve(result)
            }
            const { key } = query
            if (key) {
                const check = () => {
                    if (servable(query, readQuery(key))) done({ stored: true })
                }
                stop = subscribeQuery(key, check)
                check()
            }
            query.promise.then(
                (value) => done({ value }),
                (error) => done({ error })
            )
        })
        outcomes.set(query as Query<unknown>, outcome)
    }
    return outcome as Promise<Outcome<T>>
}

/**
 * A query's value without suspending: undefined until it is known; for
 * parts of a screen that should not hold the rest back (stats under a
 * programme). A failure leaves it undefined.
 */
export function useQueryValue<T>(query: Query<T>): T | undefined {
    const entry = useEntry(query)
    // Results the store does not keep (a fallback, an uncached value).
    const [landed, setLanded] = React.useState<{
        query: Query<T>
        value: T
    }>()
    React.useEffect(() => {
        let active = true
        query.promise.then(
            (value) => {
                // A servable stored copy already shows; anything else (a
                // fallback, an uncached value) is kept here.
                if (!active) return
                if (query.key && servable(query, readQuery(query.key))) return
                setLanded({ query, value })
            },
            () => undefined
        )
        return () => {
            active = false
        }
    }, [query])
    if (entry) return entry.value
    if (landed?.query === query) return landed.value
    return query.initial?.value
}
